import { useEffect, useRef } from "react";
import { TransformedProduct } from "../types/productApiTypes";

// The selectedAttributes type is an object where keys are attribute taxonomies (e.g., 'pa_color')
// and values are the selected term slugs (e.g., 'blue').
import type { SelectedAttributes } from "../types/productApiTypes";

/**
 * Synchronizes the selected product attributes on a single product page TO the URL query parameters.
 * This hook is one-way. It should be used in conjunction with a state management hook (like useVariationSelection)
 * that reads from the URL on initial load. This hook's job is to update the URL after user interactions.
 *
 * @param product The transformed product object.
 * @param selectedAttributes The currently selected attributes from component state.
 */
export const useSyncProductSingleAttributesToUrl = (
  product: TransformedProduct | null | undefined,
  selectedAttributes: SelectedAttributes
) => {
  const isInitialMount = useRef(true);

  useEffect(() => {
    // Skip the very first render. The state has just been initialized from the URL by
    // the useVariationSelection hook, so we don't need to immediately rewrite the URL.
    // We only want to update the URL in response to user actions *after* the initial load.
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    // Only run this logic for variable products on the frontend.
    if (!product || !product.isVariable || typeof window === "undefined") {
      return;
    }

    const url = new URL(window.location.href);
    const params = url.searchParams;

    // 1. Remove all existing `attribute_pa_*` parameters to start with a clean slate.
    for (const key of Array.from(params.keys())) {
      if (key.startsWith("attribute_pa_")) {
        params.delete(key);
      }
    }

    // 2. Add the currently selected attributes to the URL parameters.
    for (const [taxonomy, termSlug] of Object.entries(selectedAttributes)) {
      // The key in selectedAttributes is the full taxonomy (e.g., 'pa_color').
      // The URL parameter needs to be 'attribute_pa_color'.
      const paramKey = `attribute_${taxonomy}`;
      params.set(paramKey, termSlug);
    }

    // 3. Update the browser's URL without a page reload or adding to history.
    // The `toString()` method of the URL object automatically handles the '?' character.
    window.history.replaceState({}, "", url.toString());
  }, [product, selectedAttributes]); // Re-run this effect whenever the product or selections change.
};
